import React from 'react';
import { Box, Container, useTheme, useMediaQuery } from '@mui/material';
import { AuditProgressBarSkeleton } from './AuditProgressBarSkeleton';
import { AuditQuestionCardSkeleton } from './AuditQuestionCardSkeleton';

export interface AuditViewerSkeletonProps {
  questionCount?: number;
  showHeader?: boolean;
  showProgressBar?: boolean;
}

export const AuditViewerSkeleton: React.FC<AuditViewerSkeletonProps> = ({
  questionCount = 4,
  showHeader: _showHeader = true,
  showProgressBar = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine question card variants for realistic variation
  const getVariantForIndex = (index: number): 'collapsed' | 'expanded' => {
    // Show first question as expanded, rest collapsed
    return index === 0 ? 'expanded' : 'collapsed';
  };

  // Determine response counts for expanded cards
  const getResponseCountForIndex = (index: number): number => {
    // Vary response counts for visual variety (3, 5, 2, 4 pattern)
    const counts = [3, 5, 2, 4];
    return counts[index % counts.length];
  };

  // Adjust question count for mobile (show fewer skeletons)
  const displayCount = isMobile && questionCount > 3 ? 3 : questionCount;

  return (
    <Box role="status" aria-live="polite" aria-label="Loading audit data">
      {/* Main Content */}
      <Container maxWidth={false} disableGutters>
        {/* Progress Bar Section */}
        {showProgressBar && <AuditProgressBarSkeleton compact={true} showDetails={false} />}

        {/* Question Cards with Staggered Animation */}
        <Box>
          {Array.from({ length: displayCount }, (_, index) => (
            <Box
              key={index}
              sx={{
                opacity: Math.max(0.3, 1 - index * 0.15), // Staggered opacity
                animationDelay: `${index * 100}ms`, // Staggered animation delay
              }}
            >
              <AuditQuestionCardSkeleton
                variant={getVariantForIndex(index)}
                responseCount={getResponseCountForIndex(index)}
                animate={index < 2} // Only first 2 animate for performance
                isMobile={isMobile}
              />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
